import React, {useEffect, useState, useMemo, useCallback} from 'react';
import {Image, Text, TouchableOpacity, View} from 'react-native';
import Counter from './Counter/Counter';
import {styles} from './styles';
import {Images} from '../../../constants/Images';
import {useBagStore} from '../../../store/BagStore';
import {calculatePriceBasedOnQuantity} from '../../../utils/price';

import {useNavigation} from '@react-navigation/native';
import {NavigationProps} from '../../../navigation/Routes';
import {AppRoutes} from '../../../navigation/Routes.types';

interface IProductList {
  title: string;
  description: string;
  image: string;
  onPress: () => void;
  AddBag: (count: number) => void;
  item: IProduct;
}

const ProductList = React.memo(
  ({AddBag, image, item, description, onPress}: IProductList) => {
    const products = useBagStore(state => state.products);
    const [count, setCount] = useState<number>(
      products.find(prod => prod.sku === item?.sku)?.quantity || 0,
    );
    const [price, setPrice] = useState<number>(item?.preco);
    const [discountedPrice, setDiscountedPrice] = useState<number | null>(null);
    useEffect(() => {
      const {price: nPrice, discountedPrice: ndiscountedPrice} =
        calculatePriceBasedOnQuantity(item, count);

      setPrice(nPrice);
      setDiscountedPrice(ndiscountedPrice);
    }, [count, item]);

    const {navigate} = useNavigation<NavigationProps>();

    useEffect(() => {
      const foundProduct = products.find(prod => prod.sku === item?.sku);
      const suggestion = item?.suggestion_qtd || 0;

      setCount(foundProduct ? Math.max(foundProduct.quantity, suggestion) : suggestion);
    }, [products, item?.sku]);

    const formatPriceRanges = useCallback((item: IProduct) => {
      const ranges = [];
      for (let i = 2; i <= 5; i++) {
        const volume = item[`faixaVolume${i}`];
        const rangePrice = item[`kdkg${i}`];
        if (volume && rangePrice) {
          const rangeText = `${volume}+ e pague R$ ${parseFloat(
            rangePrice.toString().replace(',', '.'),
          )
            .toFixed(2)
            .replace('.', ',')} cada`;
          ranges.unshift(rangeText);
        }
      }
      return {ranges};
    }, []);

    const {ranges} = useMemo(
      () => formatPriceRanges(item),
      [item, formatPriceRanges],
    );

    const getStockStatusColor = useCallback((stock: number) => {
      if (stock > 200) {
        return '#21ce2f';
      } else if (stock > 0 && stock <= 200) {
        return '#fb6340';
      } else {
        return '#ec1f24';
      }
    }, []);

    const formatPrice = useCallback((price: string) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
      }).format(parseFloat(price));
    }, []);
    return (
      <>
        {!!item ? (
          <View style={styles.containerOneI}>
            <TouchableOpacity
              onPress={() => {
                navigate(AppRoutes.PRODUCT_DETAILS, {item: item});
              }}>
              <Image
                source={image ? {uri: image} : Images.emptyImgIcon}
                style={image ? styles.imageStyle : styles.emptyImageStyle}
              />
            </TouchableOpacity>

            <Text numberOfLines={4} style={styles.description}>
              {description}
            </Text>
            <View style={styles.infoContainer}>
              <Text numberOfLines={1} style={styles.price}>
                {formatPrice(
                  discountedPrice != null
                    ? discountedPrice.toFixed(2).toString()
                    : (price ?? 0).toFixed(2).toString(),
                )}
              </Text>
              <Text
                style={[
                  styles.hasItem,
                  {color: getStockStatusColor(item?.estoque)},
                ]}>
                {item?.estoque > 200
                  ? 'Disponível'
                  : item?.estoque > 0 && item?.estoque <= 200
                  ? 'Estoque baixo'
                  : 'Indisponível'}
              </Text>
            </View>

            {item?.estoque > 0 && ranges.length > 0 && (
              <View style={styles.priceRangesContainer}>
                {ranges.map((range, index) => (
                  <Text key={index} style={styles.priceRange}>
                    {range}
                  </Text>
                ))}
              </View>
            )}

            {item?.estoque > 0 && (
              <View style={styles.footer}>
                <Counter
                  key={item?.sku}
                  onPress={(val: number) => AddBag(val)}
                  quantity={count}
                  item={item}
                  decrement={(val: number) => setCount(val)}
                  increment={(val: number) => setCount(val)}
                  directionButton="column"
                  buttonWidth="100%"
                  max={item?.maximum ? item?.maximum : item?.estoque}
                  hasRanges
                />
              </View>
            )}
          </View>
        ) : null}
      </>
    );
  },
);

export default ProductList;
